import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  Tooltip,
  Stack,
  useMediaQuery,
  useTheme,
  Tab,
  Tabs,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ContentCopy,
  FileDownload,
  ArrowDropDown,
  DataObject,
  SubtitlesOutlined,
  TextSnippetOutlined,
} from '@mui/icons-material';
import { getTranscriptionText } from '../../utils/helpers';
import { sttApi } from '../../api/sttApi';

// ─── Time formatter (display only) ────────────────────────────────────────────

const formatTime = (seconds) => {
  if (seconds == null) return '';
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toFixed(2).padStart(5, '0');
  return `${m}:${s}`;
};

// ─── Download helper ──────────────────────────────────────────────────────────

const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const hasWordTimestamps = (job) => {
  const segments = job?.result_data?.transcription?.segments;
  return Array.isArray(segments) && segments.some((s) => Array.isArray(s.words) && s.words.length > 0);
};

const hasDiarization = (job) => {
  const segments = job?.result_data?.transcription?.segments;
  return Array.isArray(segments) && segments.some((s) => s.speaker != null);
};

// Stable color per speaker label
const SPEAKER_COLORS = ['primary', 'secondary', 'success', 'warning', 'error', 'info'];
const getSpeakerColor = (speaker, speakers) => {
  const idx = speakers.indexOf(speaker);
  return SPEAKER_COLORS[idx % SPEAKER_COLORS.length];
};

const SegmentsView = ({ segments, speakers }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {segments.map((seg) => (
      <Box key={seg.id} sx={{
        border: '1px solid',
        borderColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
        borderRadius: 1,
        p: 1.5,
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {formatTime(seg.start)} → {formatTime(seg.end)}
          </Typography>
          {seg.speaker && (
            <Chip
              label={seg.speaker}
              size="small"
              color={getSpeakerColor(seg.speaker, speakers)}
              sx={{ fontSize: '0.7rem', height: 18 }}
            />
          )}
        </Box>
        {seg.words ? (
          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {seg.words.map((w, i) => (
              <Tooltip
                key={i}
                title={`${formatTime(w.start)} → ${formatTime(w.end)}  •  ${(w.probability * 100).toFixed(0)}%`}
                placement="top"
                arrow
              >
                <Chip
                  label={w.word.trim()}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    cursor: 'default',
                    borderColor: (theme) => {
                      if (w.probability >= 0.95) return theme.palette.success.main;
                      if (w.probability >= 0.8) return theme.palette.warning.main;
                      return theme.palette.error.main;
                    },
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
            {seg.text}
          </Typography>
        )}
      </Box>
    ))}
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

const JobResultDialog = ({ open, onClose, job, onCopy }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [copySuccess, setCopySuccess] = useState(false);
  const [tab, setTab] = useState(0);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

  const segments = job?.result_data?.transcription?.segments;
  const showSegmentsTab = Array.isArray(segments) && segments.length > 0;
  const wordTimestampsPresent = hasWordTimestamps(job);
  const diarizationPresent = hasDiarization(job);
  const speakers = diarizationPresent
    ? [...new Set(segments.map((s) => s.speaker).filter(Boolean))].sort()
    : [];
  const jobSlug = job ? job.id.slice(0, 8) : 'job';

  const handleCopy = async () => {
    if (!job) return;
    const textToCopy = getTranscriptionText(job) || '';
    if (!textToCopy) return;
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const closeDownloadMenu = () => setDownloadMenuAnchor(null);

  const handleDownload = async (format) => {
    closeDownloadMenu();
    try {
      const blob = await sttApi.downloadTranscription(job.id, format);
      triggerBlobDownload(blob, `transcription_${jobSlug}.${format}`);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      data-test-id="job-result-dialog"
    >
      <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
        Transcription Result
      </DialogTitle>
      <DialogContent dividers>
        {job && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Job</Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: { xs: '0.75rem', sm: '0.875rem' }, wordBreak: 'break-all' }}
              >
                {job.id}
              </Typography>
            </Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Language</Typography>
                {(() => {
                  const t = typeof job.result_data.transcription === 'object' ? job.result_data.transcription : null;
                  const explicit = t?.language;
                  const detected = t?.detected_language;
                  const prob = t?.language_probability;
                  if (explicit) {
                    return <Typography variant="body2">{explicit}</Typography>;
                  }
                  if (detected) {
                    const pct = prob != null ? ` ${Math.round(prob * 100)}%` : '';
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                        <Typography variant="body2">{detected}</Typography>
                        <Tooltip title={`Auto-detected${pct}`}>
                          <Chip label="auto" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', cursor: 'default' }} />
                        </Tooltip>
                      </Box>
                    );
                  }
                  return <Typography variant="body2">N/A</Typography>;
                })()}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography variant="body2">
                  {(() => {
                    const d = typeof job.result_data.transcription === 'object'
                      ? job.result_data.transcription.duration
                      : job.result_data.audio_duration;
                    if (!d && d !== 0) return 'N/A';
                    const m = Math.floor(d / 60);
                    const s = Math.round(d % 60).toString().padStart(2, '0');
                    return `${m}:${s}`;
                  })()}
                </Typography>
              </Grid>
              {diarizationPresent && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Speakers</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    {speakers.map((sp) => (
                      <Chip key={sp} label={sp} size="small" color={getSpeakerColor(sp, speakers)} />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
            <Divider sx={{ my: 2 }} />

            {showSegmentsTab && (
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
                <Tab label="Text" data-test-id="job-result-tab-text" />
                <Tab label={wordTimestampsPresent ? 'Segments & Words' : 'Segments'} data-test-id="job-result-tab-segments" />
              </Tabs>
            )}

            {tab === 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Text</Typography>
                <Box
                  data-test-id="job-result-text"
                  sx={{
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                  border: '1px solid',
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                  borderRadius: 1,
                  p: { xs: 1.5, sm: 2 },
                  maxHeight: { xs: 400, sm: 300 },
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  color: 'text.primary',
                }}>
                  {getTranscriptionText(job)}
                </Box>
              </>
            )}

            {tab === 1 && showSegmentsTab && (
              <>
                {wordTimestampsPresent && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Hover over a word to see its timestamp and confidence. Color: green ≥95%, orange ≥80%, red &lt;80%.
                  </Typography>
                )}
                <Box sx={{ maxHeight: { xs: 400, sm: 450 }, overflow: 'auto' }}>
                  <SegmentsView segments={segments} speakers={speakers} />
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 },
        px: { xs: 2, sm: 2 },
        pb: { xs: 2, sm: 1 },
      }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
            <span>
              <Button
                onClick={handleCopy}
                startIcon={<ContentCopy />}
                disabled={!job}
                fullWidth={isMobile}
                data-test-id="job-result-copy-btn"
              >
                Copy
              </Button>
            </span>
          </Tooltip>

          {/* Download split-button */}
          <Button
            onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
            startIcon={<FileDownload />}
            endIcon={<ArrowDropDown />}
            disabled={!job}
            fullWidth={isMobile}
            data-test-id="job-result-download-btn"
          >
            Download
          </Button>
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={closeDownloadMenu}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            data-test-id="job-result-download-menu"
          >
            <MenuItem onClick={() => handleDownload('txt')} data-test-id="job-result-download-txt">
              <ListItemIcon><TextSnippetOutlined fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Plain text (.txt)"
                secondary="Transcription only, no timestamps"
              />
            </MenuItem>
            <MenuItem onClick={() => handleDownload('srt')} disabled={!showSegmentsTab} data-test-id="job-result-download-srt">
              <ListItemIcon><SubtitlesOutlined fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="SubRip (.srt)"
                secondary="Segment timestamps — universal subtitles"
              />
            </MenuItem>
            <MenuItem onClick={() => handleDownload('vtt')} disabled={!showSegmentsTab} data-test-id="job-result-download-vtt">
              <ListItemIcon><SubtitlesOutlined fontSize="small" /></ListItemIcon>
              <ListItemText
                primary={wordTimestampsPresent ? 'WebVTT (.vtt) — with word timestamps' : 'WebVTT (.vtt)'}
                secondary={wordTimestampsPresent
                  ? 'W3C standard, word-level cues for karaoke-style highlight'
                  : 'W3C standard subtitle format'}
              />
            </MenuItem>
            <MenuItem onClick={() => handleDownload('json')} data-test-id="job-result-download-json">
              <ListItemIcon><DataObject fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="JSON (.json)"
                secondary={wordTimestampsPresent
                  ? 'Full data incl. word timestamps & confidence — best for TTS / pipelines'
                  : 'Full structured data — best for TTS / pipelines'}
              />
            </MenuItem>
          </Menu>

          <Button
            variant="contained"
            onClick={onClose}
            fullWidth={isMobile}
            data-test-id="job-result-close-btn"
          >
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default JobResultDialog;
